import React, { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import { UserAvailability } from 'core/interfaces/userInterface';
import {
	FULLSCREEN_OVERLAY_Z_INDEX,
	getFullscreenPopperSlotProps,
	useFullscreenContainer,
} from 'core/utils/fullscreenContainer';
import { buildEmployeeWeekAvailabilityRows } from '../../utils/availabilityDisplay';

interface EmployeeAvailabilityIndicatorProps {
	employeeName?: string;
	userAvailability?: UserAvailability[];
	weekStart?: string | Date | null;
}

const EmployeeAvailabilityIndicator: React.FC<EmployeeAvailabilityIndicatorProps> = ({
	employeeName,
	userAvailability,
	weekStart,
}) => {
	const [open, setOpen] = useState(false);
	const portalContainer = useFullscreenContainer();
	const rows = useMemo(
		() => buildEmployeeWeekAvailabilityRows(userAvailability, undefined, weekStart),
		[userAvailability, weekStart]
	);

	const tooltipContent = (
		<Box sx={{ minWidth: 168, py: 0.25 }}>
			<Typography
				sx={{
					fontFamily: 'Gotham, sans-serif',
					fontSize: 12,
					fontWeight: 600,
					mb: 0.75,
					color: '#fff',
				}}
			>
				{employeeName ? `${employeeName} · Availability` : 'Availability'}
			</Typography>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
				{rows.map((row) => (
					<Box
						key={row.dayLabel}
						sx={{
							display: 'flex',
							alignItems: 'baseline',
							justifyContent: 'space-between',
							gap: 1.5,
						}}
					>
						<Typography
							sx={{
								fontFamily: 'Gotham, sans-serif',
								fontSize: 11,
								fontWeight: 600,
								color: 'rgba(255, 255, 255, 0.72)',
							}}
						>
							{row.dayShort}
						</Typography>
						<Typography
							sx={{
								fontFamily: 'Gotham, sans-serif',
								fontSize: 11,
								fontWeight: 500,
								color:
									row.kind === 'available'
										? '#fff'
										: row.kind === 'unavailable'
											? '#ffcdd2'
											: 'rgba(255, 255, 255, 0.64)',
							}}
						>
							{row.label}
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);

	return (
		<Tooltip
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			title={tooltipContent}
			arrow
			placement="right"
			enterDelay={150}
			leaveDelay={200}
			disableInteractive={false}
			slotProps={{
				...getFullscreenPopperSlotProps(portalContainer),
				tooltip: {
					sx: {
						zIndex: FULLSCREEN_OVERLAY_Z_INDEX,
						bgcolor: '#2f3b4c',
						px: 1.25,
						py: 1,
						maxWidth: 240,
					},
				},
			}}
		>
			<IconButton
				size="small"
				className="employee-availability-icon"
				aria-label={
					employeeName ? `Availability for ${employeeName}` : 'Employee availability'
				}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					setOpen((current) => !current);
				}}
				onMouseDown={(event) => event.stopPropagation()}
				onPointerDown={(event) => event.stopPropagation()}
				sx={{
					p: 0.25,
					mt: '-1px',
					color: '#6b7789',
					flex: '0 0 auto',
					'&:hover': {
						color: '#446388',
						backgroundColor: 'rgba(68, 99, 136, 0.08)',
					},
				}}
			>
				<EventAvailableOutlined sx={{ fontSize: 15 }} />
			</IconButton>
		</Tooltip>
	);
};

export default EmployeeAvailabilityIndicator;
